import axios from 'axios';

import { DPIA_CRITERIA_VERSION } from './dpiaRequests.constants';
import {
  parseCancelDpiaRequestForBE,
  parseDecideDpiaRequestForBE,
  parseDpiaHistoryForFE,
  parseDpiaRequestForFE,
  parseDpiaRequestOpenResponseForFE,
  parseOpenDpiaRequestForBE,
  parseReassessDpiaForBE,
  parseSaveDpiaRequestDraftForBE,
  parseScreeningCriteriaCatalogueForFE,
} from './dpiaRequests.parsers';
import type {
  CancelDpiaRequestInput,
  DecideDpiaRequestInput,
  DpiaReassess,
  DpiaRequestDTO,
  DpiaRequestEnvelopeDTO,
  DpiaRequestsApi,
  DpiaScreeningCriteriaCatalogueDTO,
  OpenDpiaRequestInput,
  ReassessDpiaInput,
  SaveDpiaRequestDraftInput,
} from './dpiaRequests.types';

const buildOpenEndpoint = (organizationId: number, assetProcessingActivityId: number) =>
  `api/v1/organizations/${organizationId}/asset-processing-activities/${assetProcessingActivityId}/dpia-requests/open/`;

const buildRequestEndpoint = (organizationId: number, requestId: number) =>
  `api/v1/organizations/${organizationId}/dpia-requests/${requestId}/`;

const buildDecideRequiredEndpoint = (organizationId: number, requestId: number) =>
  `${buildRequestEndpoint(organizationId, requestId)}decide-required/`;

const buildDecideNotRequiredEndpoint = (organizationId: number, requestId: number) =>
  `${buildRequestEndpoint(organizationId, requestId)}decide-not-required/`;

const buildCancelEndpoint = (organizationId: number, requestId: number) => `${buildRequestEndpoint(organizationId, requestId)}cancel/`;

const buildScreeningCriteriaEndpoint = (organizationId: number, version = DPIA_CRITERIA_VERSION) =>
  `api/v1/organizations/${organizationId}/dpia-screening-criteria/?version=${encodeURIComponent(version)}`;

const buildDpiaHistoryEndpoint = (organizationId: number, assetProcessingActivityId: number) =>
  `api/v1/organizations/${organizationId}/asset-processing-activities/${assetProcessingActivityId}/dpia-history/`;

const buildReassessEndpoint = (organizationId: number, assetProcessingActivityId: number) =>
  `api/v1/organizations/${organizationId}/asset-processing-activities/${assetProcessingActivityId}/dpia-requests/reassess/`;

export const dpiaRequestsApi: DpiaRequestsApi = {
  getScreeningCriteria: async (organizationId, version = DPIA_CRITERIA_VERSION) => {
    const { data } = await axios.get<DpiaScreeningCriteriaCatalogueDTO>(buildScreeningCriteriaEndpoint(organizationId, version));
    return { data: parseScreeningCriteriaCatalogueForFE(data) };
  },
  getDpiaHistory: async (organizationId, assetProcessingActivityId) => {
    const { data } = await axios.get(buildDpiaHistoryEndpoint(organizationId, assetProcessingActivityId));
    return { data: parseDpiaHistoryForFE(data) };
  },
  reassess: async (organizationId, assetProcessingActivityId, payload: ReassessDpiaInput) => {
    const { data } = await axios.post<DpiaReassess>(
      buildReassessEndpoint(organizationId, assetProcessingActivityId),
      parseReassessDpiaForBE(payload),
    );
    return { data: { reason: typeof data?.reason === 'string' ? data.reason : payload.reason.trim() } };
  },
  openRequest: async (organizationId, assetProcessingActivityId, payload: OpenDpiaRequestInput = {}) => {
    const { data } = await axios.post<DpiaRequestEnvelopeDTO>(
      buildOpenEndpoint(organizationId, assetProcessingActivityId),
      parseOpenDpiaRequestForBE(payload),
    );
    return { data: parseDpiaRequestOpenResponseForFE(data) };
  },
  getRequest: async (organizationId, requestId) => {
    const { data } = await axios.get<DpiaRequestEnvelopeDTO>(buildRequestEndpoint(organizationId, requestId));
    return { data: parseDpiaRequestOpenResponseForFE(data) };
  },
  saveDraft: async (organizationId, requestId, payload: SaveDpiaRequestDraftInput) => {
    const { data } = await axios.patch<DpiaRequestDTO>(
      buildRequestEndpoint(organizationId, requestId),
      parseSaveDpiaRequestDraftForBE(payload),
    );
    return { data: parseDpiaRequestForFE(data) };
  },
  decideRequired: async (organizationId, requestId, payload: DecideDpiaRequestInput) => {
    const { data } = await axios.post<DpiaRequestDTO>(
      buildDecideRequiredEndpoint(organizationId, requestId),
      parseDecideDpiaRequestForBE(payload),
    );
    return { data: parseDpiaRequestForFE(data) };
  },
  decideNotRequired: async (organizationId, requestId, payload: DecideDpiaRequestInput) => {
    const { data } = await axios.post<DpiaRequestDTO>(
      buildDecideNotRequiredEndpoint(organizationId, requestId),
      parseDecideDpiaRequestForBE(payload),
    );
    return { data: parseDpiaRequestForFE(data) };
  },
  cancelRequest: async (organizationId, requestId, payload: CancelDpiaRequestInput) => {
    const { data } = await axios.post<DpiaRequestDTO>(buildCancelEndpoint(organizationId, requestId), parseCancelDpiaRequestForBE(payload));
    return { data: parseDpiaRequestForFE(data) };
  },
};

export default dpiaRequestsApi;
